import * as Sentry from '@sentry/react';
import { CustomerPortalApiError } from './customerPortalApiError';

type CustomerPortalApiParams = {
  url: string | URL;
  options?: RequestInit;
  accessToken: Promise<string>;
  transactionId: string;
  type?: 'json' | 'text' | 'blob';
  updateProfile: () => Promise<void>;
  excludeHeaders?: string[];
  callerName?: string;
};

export async function customerPortalApi<ReturnDataType>({
  url,
  options,
  accessToken,
  transactionId,
  type = 'json',
  updateProfile,
  excludeHeaders = [],
  callerName,
}: CustomerPortalApiParams): Promise<ReturnDataType> {
  const fetchOptions = options ?? {};

  const headers = new Headers(fetchOptions.headers);

  headers.set('Authorization', `Bearer ${await accessToken}`);
  headers.set('X-Transaction-ID', transactionId);

  // If body is supplied and method is not, default to POST
  if (fetchOptions.body) {
    fetchOptions.method = fetchOptions.method ?? 'POST';
  }

  // If method is POST, PUT, PATCH or DELETE and body is supplied, default Content-Type to application/json
  if (
    ['POST', 'PUT', 'PATCH', 'DELETE'].includes(fetchOptions.method ?? '') &&
    fetchOptions.body &&
    !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json');
  }

  // Remove any headers that should be excluded
  excludeHeaders.forEach((header) => {
    headers.delete(header);
  });

  fetchOptions.headers = headers;

  try {
    const response = await fetch(url, fetchOptions);

    // Return response if ok (status in the range 200-299)
    if (response.ok) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return await response[type]();
    }

    if (response.status === 403) {
      console.warn('Access forbidden - updating profile and retrying might help');
      await updateProfile();
    }

    const responseBody = await response.text().catch(() => 'Backend responded with non-ok status.');
    // Handle non-2xx responses (Errors to exclude from Sentry are defined in initSentry.ts)
    const sentryHint = {
      extra: {
        url,
        status: response.status,
        statusText: response.statusText,
        method: fetchOptions.method,
        body: fetchOptions.body,
        responseBody,
        headers: Object.fromEntries(headers.entries()),
      },
    };

    const statusText = getDefaultStatusText(response.status);

    const error = new CustomerPortalApiError({
      status: response.status,
      statusText,
      url,
      method: fetchOptions.method,
      responseBody,
      callerName,
    });

    console.error(error);

    // Exclude this function from the stack trace to make it cleaner in Sentry
    Error.captureStackTrace(error, customerPortalApi);

    Sentry.captureException(error, sentryHint);
    throw error;
  } catch (error) {
    const alreadyCaptured = error instanceof CustomerPortalApiError;
    const isAbort = error instanceof Error && error.name === 'AbortError';

    // Log network errors or other unexpected errors
    if (!alreadyCaptured && !isAbort) {
      Sentry.captureException(error, {
        extra: {
          url,
          method: fetchOptions.method,
          body: fetchOptions.body,
          headers: Object.fromEntries(fetchOptions.headers.entries()),
          userIsOnline: navigator.onLine,
        },
        tags: {
          fetch_error: true,
          network_offline: !navigator.onLine,
        },
      });
    }

    throw error;
  }
}

function getDefaultStatusText(status: number): string {
  return STATUS_TEXT[status] ?? 'Unknown Error';
}

const STATUS_TEXT: Record<number, string> = {
  400: 'Bad Request',
  401: 'Unauthorized',
  402: 'Payment Required',
  403: 'Forbidden',
  404: 'Not Found',
  405: 'Method Not Allowed',
  406: 'Not Acceptable',
  408: 'Request Timeout',
  409: 'Conflict',
  410: 'Gone',
  411: 'Length Required',
  412: 'Precondition Failed',
  413: 'Payload Too Large',
  415: 'Unsupported Media Type',
  418: "I'm a teapot", // optional fun
  422: 'Unprocessable Entity',
  429: 'Too Many Requests',

  500: 'Internal Server Error',
  501: 'Not Implemented',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
  504: 'Gateway Timeout',
};
